(ns frontend.view.modals.options-management.events
  (:require
   [clojure.data :as data]
   [clojure.string :as string]
   [common.localization :as loc]
   [frontend.controller.websocket.event :as ws]
   [frontend.logging :as logging]
   [frontend.state.current-session :as session]
   [frontend.utils :as fu]
   [frontend.view.modals.error-message.view :as error-message-modal]
   [frontend.view.modals.events :as modals]
   [frontend.view.modals.options-management.subs :as subs]
   [oops.core :as oops]
   [potpuri.core :as p]
   [re-frame.core :as rf]
   [ui.state.named-values :as named-values]))

;;REFRAME EVENTS

(defn cleanup
  [{db :db} [_ {:keys [close-modal!]} _response]]
  ;;TODO update db if backend returns updated entities
  {:db                  (-> db
                            (dissoc ::subs/state))
   ;; The shared option lists were fetched before this save. Drop them. The next
   ;; reader then fetches the saved rows and not the old copy.
   ::invalidate-options (get-in db [::subs/state ::subs/selected-field])
   ::modals/close-modal close-modal!})

(rf/reg-event-fx
 ::save-ok
 cleanup)

(rf/reg-fx
 ::invalidate-options
 (fn [attribute]
   (when attribute
     (named-values/invalidate! attribute))))

(defn error-received-fx
  [db [_ {:keys [close-modal!]} response]]
  (let [open-error-modal! (fn [] (modals/open-modal!
                                  error-message-modal/modal-key
                                  {:title          (loc/tr :attention)
                                   :body-content   (:message response)
                                   :ok-button-text (loc/tr :ok)}))]
    {:db                  db
     ::modals/close-modal close-modal!                                                              ;;closes the options modal
     ::modals/open-modal  open-error-modal!}))

(rf/reg-event-fx
 ::error-received
 error-received-fx)

(defn open-error-modal!
  [error-message]
  (modals/open-modal!
   error-message-modal/modal-key
   {:title          (loc/tr :attention)
    :body-content   [:div.errors
                     (for [msg error-message]
                       ^{:key (str msg)}
                       [:div (str msg)])]
    :ok-button-text (loc/tr :ok)}))

(defn save-changes-fx
  [{db :db} [_ {:keys [_close-modal!] :as args}]]
  (let [named-values (get-in db [::subs/state ::subs/named-values])
        selected-field (get-in db [::subs/state ::subs/selected-field])
        original-named-values (set (get-in db [::subs/state ::subs/original-named-values]))
        [_ new-data _] (data/diff original-named-values (set named-values))
        new-options (->> new-data
                         (remove :db/id))
        changed-options (->> new-data
                             (filter :db/id))
        fields-to-update #{:named-value/value
                           :named-value/archived-in-pipelines
                           :common/ord
                           :common/archived?}
        changes
        (->> changed-options
             (mapv (fn [changed-entity]
                     (let [changed-entity-id (:db/id changed-entity)
                           old-entity (->> original-named-values
                                           (filter (fn [original]
                                                     (= changed-entity-id (:db/id original))))
                                           (first))
                           [removed-fields new-fields _] (data/diff old-entity changed-entity)
                           ;; removed or updated attributes
                           changed-attributes (->> (distinct (concat (keys removed-fields)
                                                                     (keys new-fields)))
                                                   (filter fields-to-update))
                           update-result (merge {:db/id changed-entity-id}
                                                (select-keys changed-entity changed-attributes))]
                       update-result))))
        named-value-labels (subs/named-value-labels named-values nil)
        duplicate-values (subs/duplicate-named-values named-value-labels nil)
        empty-values? (->> named-value-labels
                           (some string/blank?))
        errors? (or empty-values?
                    duplicate-values)
        error-message (subs/error-message [empty-values? duplicate-values true] nil)
        new-options-to-send (->> new-options
                                 (mapv (fn [o]
                                         (select-keys
                                          o
                                          [:named-value/value
                                           :common/ord
                                           :named-value/archived-in-pipelines
                                           :common/archived?]))))
        bulk-update-data {:named-value-attribute selected-field
                          :new-options           new-options-to-send
                          :updated-options       changes}]
    (if errors?
      (open-error-modal! error-message)
      (do
        {:db (assoc-in db [::subs/state ::subs/saving?] true)
         :fx [[::ws/send {:id         :named-value/bulk-update
                          :body       bulk-update-data
                          :on-success [::save-ok args]
                          :on-error   [::error-received args]}]]}))))

(rf/reg-event-fx
 ::save-changes
 save-changes-fx)

(defn clear
  [db [_]]
  (-> db
      (dissoc ::subs/state)))

(rf/reg-event-db
 ::clear
 clear)

(defn create-new-draft-option
  [db [_]]
  (let [named-values (get-in db [::subs/state ::subs/named-values])
        max-ord (->> named-values
                     (mapv :common/ord)
                     (apply max 1.0)
                     inc)
        selected-field (get-in db [::subs/state ::subs/selected-field])]
    (assoc-in db
              [::subs/state ::subs/named-values]
              (vec (conj named-values
                         {:entity/type           :entity.type/named-value
                          :named-value/attribute selected-field
                          :named-value/value     ""
                          :common/ord            max-ord})))))

(rf/reg-event-db
 ::create-new-draft-option
 create-new-draft-option)

(defn merge-options
  [db [_ {:keys [victim-id]}]]
  (let [remaining-nvs (->> (get-in db [::subs/state ::subs/named-values])
                           (remove (fn [option]
                                     (= victim-id (:db/id option))))
                           (vec))]
    (-> db
        (assoc-in [::subs/state ::subs/named-values] remaining-nvs)
        (p/dissoc-in [::subs/state ::subs/selected-row-id])
        (p/dissoc-in [::subs/state ::subs/selected-row-ord]))))

(rf/reg-event-db
 ::merge-options
 merge-options)

(defn delete-option
  [db [_ {:keys [option-id ord]}]]
  (let [named-values (get-in db [::subs/state ::subs/named-values])
        values-after-remove (if option-id
                              (->> named-values
                                   (remove (fn [option]
                                             (= option-id (:db/id option)))))
                              (->> named-values
                                   (remove (fn [option]
                                             (= ord (:common/ord option))))))
        updated-values (->> values-after-remove
                            (vec))]
    (-> db
        (assoc-in [::subs/state ::subs/named-values] updated-values)
        (p/dissoc-in [::subs/state ::subs/selected-row-id])
        (p/dissoc-in [::subs/state ::subs/selected-row-ord]))))

(rf/reg-event-db
 ::delete-option
 delete-option)

(defn set-selected-row
  [db [_ params]]
  (let [grid-api (oops/oget params "api")
        selected-nodes-js (oops/ocall grid-api "getSelectedNodes")
        selected-nodes (js->clj selected-nodes-js :keywordize-keys true)
        selected-node (first selected-nodes)
        selected-node-data (when selected-node (js->clj (oops/oget selected-node "data") {:keywordize-keys true}))
        selected-node-id (:id selected-node-data)
        selected-node-ord (:ord selected-node-data)]
    (if selected-node
      (-> db
          (assoc-in [::subs/state ::subs/selected-row-id] selected-node-id)
          (assoc-in [::subs/state ::subs/selected-row-ord] selected-node-ord))
      (-> db
          (p/dissoc-in [::subs/state ::subs/selected-row-id])
          (p/dissoc-in [::subs/state ::subs/selected-row-ord])))))

(rf/reg-event-db
 ::set-selected-row
 set-selected-row)

(defn disable-row-dragging
  [db [_ params]]
  (let [column-api (oops/oget params "api")
        column-state-js (oops/ocall! column-api "getColumnState")
        column-state (js->clj column-state-js :keywordize-keys true)
        sorting-disabled? (->> column-state
                               (keep :sort)
                               (empty?))]
    (-> db
        (assoc-in [::subs/state ::subs/row-dragging-disabled?] (not sorting-disabled?)))))

(rf/reg-event-db
 ::disable-row-dragging
 disable-row-dragging)

(defn reordered-options
  [{:keys [old-common-ord new-common-ord common-ord-before-new common-ord-after-new named-values]}]
  (let [move-down? (> new-common-ord old-common-ord)
        max-ord (->> named-values (mapv :common/ord) (apply max 1.0))
        calculated-new-ord (cond (or (nil? common-ord-after-new) (nil? new-common-ord))             ;; dragged to last
                                 (inc max-ord)
                                 move-down?
                                 (/ (+ new-common-ord common-ord-after-new) 2)
                                 :else
                                 (/ (+ new-common-ord common-ord-before-new) 2))
        sorted-vec (->> named-values
                        (map (fn [nv]
                               (let [ord (:common/ord nv)]
                                 (cond
                                   (= ord 0)
                                   (assoc nv :common/ord (/ calculated-new-ord 2))
                                   (= ord old-common-ord)
                                   (assoc nv :common/ord calculated-new-ord)
                                   :else
                                   (assoc nv :common/ord ord)))))
                        (sort-by :common/ord)
                        (vec))]
    sorted-vec))

(defn reorder-on-drag-end
  [db [_ params]]
  (let [named-values (get-in db [::subs/state ::subs/named-values])
        node (oops/oget params "node")
        old-index (oops/oget node "rowIndex")
        over-node (oops/oget params "overNode")
        new-index (when-not (nil? over-node) (oops/oget over-node "rowIndex"))
        old-common-ord (when (number? old-index) (get-in named-values [old-index :common/ord]))
        new-common-ord (when (number? new-index) (get-in named-values [new-index :common/ord]))
        common-ord-before-new (when (number? new-index) (get-in named-values [(dec new-index) :common/ord]))
        common-ord-after-new (when (number? new-index) (get-in named-values [(inc new-index) :common/ord]))]
    (if (= old-index new-index)
      db
      (-> db
          (assoc-in [::subs/state ::subs/named-values]
                    (reordered-options
                     {:old-common-ord        old-common-ord
                      :new-common-ord        new-common-ord
                      :common-ord-before-new common-ord-before-new
                      :common-ord-after-new  common-ord-after-new
                      :named-values          named-values}))
          (p/dissoc-in [::subs/state ::subs/row-to-highlight])))))

(rf/reg-event-db
 ::reorder-on-drag-end
 reorder-on-drag-end)

(defn on-row-drag-move
  [db [_ params]]
  (let [over-node (oops/oget params "overNode")
        over-index (when-not (nil? over-node) (oops/oget over-node "rowIndex"))]
    (assoc-in db [::subs/state ::subs/row-to-highlight] over-index)))

(rf/reg-event-db
 ::on-row-drag-move
 on-row-drag-move)

(defn update-named-value
  [db [_ {:keys [index attribute new-value]}]]
  (assoc-in db [::subs/state ::subs/named-values index attribute] new-value))

(rf/reg-event-db
 ::update-named-value
 update-named-value)

(defn update-named-value-on-cell-edit
  [db [_ params]]
  (let [new-value (oops/oget params "newValue")
        node (oops/oget params "node")
        index-to-edit (oops/oget node "rowIndex")]                                                  ;;Assuming common/ord is never zero
    (-> db
        (assoc-in [::subs/state ::subs/named-values index-to-edit :named-value/value] new-value))))

(rf/reg-event-db
 ::update-named-value-on-cell-edit
 update-named-value-on-cell-edit)

(defn- sorted-options
  [rows]
  (some->> rows
           (sort-by :common/ord)
           (vec)))

(defn- loaded-state
  "The rows that the user edits, and the unchanged copy that the diff uses."
  [rows]
  (let [sorted-rows (sorted-options rows)]
    {::subs/named-values          sorted-rows
     ::subs/original-named-values sorted-rows
     ::subs/initialized?          true}))

(defn initialize-state-from-args
  [{db :db} [_ {:keys [pipeline-attribute selected-field]}]]
  (let [pipelines (fu/get-pipelines-by-attribute pipeline-attribute)
        base-state {::subs/pipelines      pipelines
                    ::subs/selected-field selected-field}]
    (if (session/bulk-synced-entity-type? :entity.type/named-value)
      {:db (assoc-in db [::subs/state]
                     (merge base-state (loaded-state (session/named-values selected-field))))}
      ;; The session has only the options that an open entity points at. The
      ;; modal would show some of the options, and "Add option" could pick an
      ;; order number that is already in use. Fetch the whole list and wait for it.
      {:db                 (assoc-in db [::subs/state]
                                     (assoc base-state ::subs/initialized? false))
       ::load-options-from {:attribute selected-field}})))

(rf/reg-event-fx
 ::init-options-data
 initialize-state-from-args)

(rf/reg-fx
 ::load-options-from
 (fn [{:keys [attribute]}]
   (-> (named-values/load-all! attribute)
       (.then (fn [rows]
                (rf/dispatch [::options-loaded {:attribute attribute
                                                :rows      rows}])))
       (.catch (fn [error]
                 (logging/error "Manage options: loading the options failed for "
                                attribute " >> " error)
                 (rf/dispatch [::options-loaded {:attribute attribute
                                                 :rows      []}]))))))

(defn options-loaded
  [db [_ {:keys [attribute rows]}]]
  ;; The user could close the modal, or open it for another field, in the meantime.
  (if (= attribute (get-in db [::subs/state ::subs/selected-field]))
    (update-in db [::subs/state] merge (loaded-state rows))
    db))

(rf/reg-event-db
 ::options-loaded
 options-loaded)
